import { useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { getMe, updateProfile } from '../../api/authApi';

const cyan = '#00e5ff';

const labelStyle = {
  color: '#888',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  display: 'block',
  marginBottom: 6,
};

const inputStyle = (disabled) => ({
  width: '100%',
  background: disabled ? 'rgba(255,255,255,0.03)' : 'rgba(0,229,255,0.04)',
  border: `1px solid ${disabled ? 'rgba(255,255,255,0.06)' : 'rgba(0,229,255,0.2)'}`,
  borderRadius: 8,
  padding: '10px 14px',
  color: disabled ? '#555' : '#fff',
  fontSize: 14,
  outline: 'none',
  cursor: disabled ? 'not-allowed' : 'text',
});

const Profile = () => {
  const [form, setForm] = useState({ name: '', email: '', mobile: '', usdtAddress: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getMe()
      .then((res) => {
        const u = res.data.user;
        setForm({
          name:        u.name        || '',
          email:       u.email       || '',
          mobile:      u.mobile      || '',
          usdtAddress: u.usdtAddress || '',
        });
      })
      .catch(() => setError('Failed to load profile.'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setSuccess('');
    setError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Name is required.');
    if (!form.mobile.trim()) return setError('Mobile number is required.');
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateProfile({ name: form.name, mobile: form.mobile, usdtAddress: form.usdtAddress });
      setSuccess('Profile updated successfully.');
    } catch (err) {
      setError(err.response?.data?.message || 'Update failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Container className="mt-5 text-center">
      <Spinner animation="border" style={{ color: cyan }} />
    </Container>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4" style={{ maxWidth: 520 }}>
        <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>My Profile</h5>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 28 }}>
          Update your personal information and USDT wallet address.
        </div>

        <div style={{
          background: 'radial-gradient(ellipse at 100% 0%, rgba(0,229,255,0.06) 0%, #0d0d0d 65%)',
          border: '1px solid rgba(0,229,255,0.15)',
          borderRadius: 14,
          padding: '28px 24px',
        }}>
          {error && (
            <div style={{ background: 'rgba(200,50,50,0.12)', border: '1px solid rgba(200,50,50,0.3)', color: '#ff6666', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{ background: 'rgba(102,255,153,0.1)', border: '1px solid rgba(102,255,153,0.3)', color: '#66ff99', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 }}>
              {success}
            </div>
          )}

          <form onSubmit={handleSave}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Full Name</label>
              <input
                name="name"
                type="text"
                value={form.name}
                onChange={handleChange}
                style={inputStyle(false)}
                placeholder="Your full name"
                required
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                value={form.email}
                disabled
                style={inputStyle(true)}
              />
              <div style={{ color: '#444', fontSize: 11, marginTop: 5 }}>Email cannot be changed.</div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Mobile Number</label>
              <input
                name="mobile"
                type="tel"
                value={form.mobile}
                onChange={handleChange}
                style={inputStyle(false)}
                placeholder="Your mobile number"
                required
              />
            </div>

            <div style={{ marginBottom: 28 }}>
              <label style={labelStyle}>USDT Wallet Address (External)</label>
              <input
                name="usdtAddress"
                type="text"
                value={form.usdtAddress}
                onChange={handleChange}
                style={inputStyle(false)}
                placeholder="e.g. TXxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
              <div style={{ color: '#444', fontSize: 11, marginTop: 5 }}>
                Used for external USDT withdrawals (TRC-20 / ERC-20).
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 8,
                border: 'none',
                background: saving ? 'rgba(255,255,255,0.06)' : cyan,
                color: saving ? '#555' : '#000',
                fontWeight: 700,
                fontSize: 15,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      </Container>
    </div>
  );
};

export default Profile;
